import type { SimulationCueSheetRow } from '@/types/player/simulation-cue-sheet.types';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import {
  enrichSimulationRowAdvanceFromPlaylist,
  isReturnPreviousAdvanceSettings,
} from '@/helpers/show-info-advance.helper';

/** Stable key for session one-shot played markers (prefer live cueItemId). */
export function simulationRowKey(row: SimulationCueSheetRow | undefined, index: number): string {
  if (!row) {
    return `index:${index}`;
  }
  if (row.cueItemId) {
    return row.cueItemId;
  }
  return `ordinal:${row.ordinal}:${row.itemId}:${row.scope ?? ''}:${row.slotId ?? ''}`;
}

export function isSimulationReturnPreviousRow(row: SimulationCueSheetRow | undefined, flow?: FlowDto): boolean {
  if (!row) {
    return false;
  }
  const enriched = flow ? enrichSimulationRowAdvanceFromPlaylist(flow, row) : row;
  return isReturnPreviousAdvanceSettings(enriched.advanceSettings);
}

/** Walk backward from `fromIndex` to the nearest non-return_previous row. */
export function resolveSimulationMainPointer(
  rows: SimulationCueSheetRow[],
  fromIndex: number,
  flow?: FlowDto,
): number | null {
  if (fromIndex < 0 || fromIndex >= rows.length) {
    return null;
  }
  let index = fromIndex;
  while (index >= 0) {
    if (!isSimulationReturnPreviousRow(rows[index], flow)) {
      return index;
    }
    index -= 1;
  }
  return null;
}

/** Contiguous return_previous rows immediately after the main row. */
export function resolveSimulationAsideRunKeys(
  rows: SimulationCueSheetRow[],
  mainIndex: number,
  flow?: FlowDto,
): string[] {
  if (mainIndex < 0 || mainIndex >= rows.length) {
    return [];
  }
  if (isSimulationReturnPreviousRow(rows[mainIndex], flow)) {
    return [];
  }
  const keys: string[] = [];
  for (let i = mainIndex + 1; i < rows.length; i += 1) {
    const row = rows[i]!;
    if (!isSimulationReturnPreviousRow(row, flow)) {
      break;
    }
    keys.push(simulationRowKey(row, i));
  }
  return keys;
}

export function isSimulationRowInAsideRunOfMain(
  rows: SimulationCueSheetRow[],
  mainIndex: number,
  rowIndex: number,
  flow?: FlowDto,
): boolean {
  const key = simulationRowKey(rows[rowIndex], rowIndex);
  return resolveSimulationAsideRunKeys(rows, mainIndex, flow).includes(key);
}

export function markSimulationPlayedReturnPrevious(played: string[] | undefined, key: string): string[] {
  if (played?.includes(key)) {
    return played;
  }
  return [...(played ?? []), key];
}

export function clearSimulationPlayedReturnPreviousFromIndex(
  rows: SimulationCueSheetRow[],
  fromIndex: number,
  played: string[] | undefined,
  flow?: FlowDto,
): string[] | undefined {
  if (!played?.length) {
    return played;
  }
  const clearKeys = new Set<string>();
  for (let i = Math.max(0, fromIndex); i < rows.length; i += 1) {
    if (isSimulationReturnPreviousRow(rows[i], flow)) {
      clearKeys.add(simulationRowKey(rows[i], i));
    }
  }
  if (clearKeys.size === 0) {
    return played;
  }
  const next = played.filter((key) => !clearKeys.has(key));
  return next.length === played.length ? played : next;
}

export function findSimulationPointerByRowKey(
  rows: SimulationCueSheetRow[],
  key: string | null | undefined,
): number | null {
  if (!key) {
    return null;
  }
  const index = rows.findIndex((row, i) => simulationRowKey(row, i) === key);
  return index >= 0 ? index : null;
}
